import { post, request } from "~/common/request"

export const watermarkStaffVideo = async (
  params: API.Staff.WatermarkVideoParams,
  signal?: AbortSignal,
): Promise<Blob> => {
  const file = await request
    .post("/api/staff/lab/watermark_video", {
      json: params,
      headers: { accept: "video/mp4" },
      context: { notify: false, responseType: "blob" },
      timeout: 300_000,
      signal,
    })
    .blob()

  if (file.type.split(";")[0].trim().toLowerCase() !== "video/mp4")
    throw new Error("生成失败：接口未返回 MP4 视频，请重试")
  if (!file.size) throw new Error("生成失败：返回的视频为空，请重试")
  return file
}

export const listStaffVideoTemplates = (
  params: API.VideoTemplate.StaffListParams = {},
  signal?: AbortSignal,
) => {
  return post<API.VideoTemplate.ListResponse>(
    "/api/staff/video-template/list",
    params,
    {
      notify: false,
      signal,
    },
  )
}

export const upsertStaffVideoTemplate = (params: API.VideoTemplate.Content) => {
  const content = { ...params }
  delete content.alias
  return post<API.VideoTemplate.Content>(
    "/api/staff/video-template/upsert",
    content,
    {
      notify: false,
    },
  )
}

export const listStaffTemplateCategories = (
  params: API.VideoTemplate.CategoryListParams = {},
  signal?: AbortSignal,
) =>
  post<API.VideoTemplate.CategoryListResponse>(
    "/api/staff/video-template/category/list",
    params,
    { notify: false, signal },
  )

export const deleteStaffVideoTemplate = (
  params: API.VideoTemplate.DeleteParams,
) => post<null>("/api/staff/video-template/delete", params, { notify: false })

export const deleteStaffTemplateCategory = (
  params: API.VideoTemplate.DeleteCategoryParams,
  signal?: AbortSignal,
) =>
  post<null>("/api/staff/video-template/category/delete", params, {
    notify: false,
    signal,
  })

export const upsertStaffTemplateCategory = (
  params: API.VideoTemplate.UpsertCategoryParams,
  signal?: AbortSignal,
) =>
  post<API.VideoTemplate.UpsertCategoryParams>(
    "/api/staff/video-template/category/upsert",
    params,
    { notify: false, signal },
  )

export const previewScriptFusion = (
  params: API.Staff.ScriptFusionPreviewParams,
  signal?: AbortSignal,
) =>
  post<API.Staff.ScriptFusionPreview>(
    "/api/staff/task/preview_script_fusion",
    params,
    { signal, notify: false },
  )

export const createStaffHotVideo = (
  params: API.Staff.HotVideoCreateVideoParams,
) => {
  // Staff 预览可能包含内网资源，直接提交 URL，由后端读取和处理资源。
  return post<API.Task.HotVideoTask>(
    "/api/staff/task/hot-video/create-video",
    params,
  )
}

export const getStaffVideoFrame = (params: API.Staff.GetVideoFrameParams) => {
  return post<API.Staff.GetVideoFrameResponse, API.Staff.GetVideoFrameParams>(
    "/api/staff/lab/get_video_frame",
    params,
    {
      notify: false,
    },
  )
}

export const listStaffUsers = (
  params: API.Staff.StaffUserListParams,
  signal?: AbortSignal,
) => {
  return post<API.Staff.StaffUserListResponse, API.Staff.StaffUserListParams>(
    "/api/staff/user/list",
    params,
    {
      signal,
    },
  )
}

export const listStaffUserVideoTasks = (
  params: API.Staff.StaffVideoTaskListParams,
  signal?: AbortSignal,
) => {
  return post<
    API.Staff.StaffVideoTaskListResponse,
    API.Staff.StaffVideoTaskListParams
  >("/api/staff/task/list_user_video_tasks", params, {
    signal,
  })
}

export const listStaffFrameCopyTasks = (
  params: API.Staff.StaffFrameCopyTaskListParams,
  signal?: AbortSignal,
) => {
  return post<
    API.Staff.StaffFrameCopyTaskListResponse,
    API.Staff.StaffFrameCopyTaskListParams
  >("/api/staff/task/list_frame_copy_tasks", params, {
    signal,
  })
}

export const listStaffEnhanceVideoTasks = (
  params: API.Staff.StaffEnhanceVideoTaskListParams,
  signal?: AbortSignal,
) => {
  return post<
    API.Staff.StaffEnhanceVideoTaskListResponse,
    API.Staff.StaffEnhanceVideoTaskListParams
  >("/api/staff/task/list_enhance_video_tasks", params, {
    signal,
  })
}

export const listStaffEraseVideoSubtitleTasks = (
  params: API.Staff.StaffEraseVideoSubtitleTaskListParams,
  signal?: AbortSignal,
) => {
  return post<
    API.Staff.StaffEraseVideoSubtitleTaskListResponse,
    API.Staff.StaffEraseVideoSubtitleTaskListParams
  >("/api/staff/task/list_erase_video_subtitle_tasks", params, {
    signal,
  })
}

export const getStaffRawTaskDetail = (
  params: API.Staff.StaffRawTaskDetailParams,
  signal?: AbortSignal,
) => {
  return post<API.Staff.StaffRawTask, API.Staff.StaffRawTaskDetailParams>(
    "/api/staff/task/raw_task/detail",
    params,
    {
      signal,
    },
  )
}

export const changeStaffUser = (params: API.Staff.ChangeStaffUserParams) => {
  return post<
    API.Staff.ChangeStaffUserResponse,
    API.Staff.ChangeStaffUserParams
  >("/api/staff/user/change", params)
}

export const createStaffGiftOpLog = (
  params: API.Staff.CreateStaffGiftOpLogParams,
) => {
  return post<API.Staff.StaffGiftOpLog, API.Staff.CreateStaffGiftOpLogParams>(
    "/api/staff/gift/create_staff_gift_op_log",
    params,
  )
}

export const createStaffLevelGiftOpLog = (
  params: API.Staff.CreateStaffLevelGiftOpLogParams,
) => {
  return post<
    API.Staff.StaffGiftOpLog,
    API.Staff.CreateStaffLevelGiftOpLogParams
  >("/api/staff/gift/create_staff_level_gift_op_log", params)
}

export const createStaffActivityGiftOpLog = (
  params: API.Staff.CreateStaffActivityGiftParams,
) => {
  return post<
    API.Staff.StaffGiftOpLog,
    API.Staff.CreateStaffActivityGiftParams
  >("/api/staff/gift/create_staff_activity_gift_op_log", params)
}

export const revokeStaffLevelGift = (
  params: API.Staff.RevokeStaffLevelGiftParams,
) => {
  return post<
    API.Staff.RevokeStaffLevelGiftResponse,
    API.Staff.RevokeStaffLevelGiftParams
  >("/api/staff/gift/revoke_staff_level_gift", params)
}

export const getStaffGiftOpDetail = (
  params: API.Staff.StaffGiftOpLogDetailParams,
  signal?: AbortSignal,
) => {
  return post<
    API.Staff.StaffGiftOpLogDetailResponse,
    API.Staff.StaffGiftOpLogDetailParams
  >("/api/staff/gift/get_staff_gift_op_detail", params, {
    signal,
  })
}

export const listStaffGiftOpLogs = (
  params: API.Staff.StaffGiftOpLogListParams = {},
  signal?: AbortSignal,
) => {
  return post<
    API.Staff.StaffGiftOpLogListResponse,
    API.Staff.StaffGiftOpLogListParams
  >("/api/staff/gift/get_staff_gift_op_log", params, {
    signal,
  })
}

export const listDoudianSubscriptionOrders = (
  params: API.Staff.DoudianSubscriptionOrderListParams = {},
  signal?: AbortSignal,
) => {
  return post<
    API.Staff.DoudianSubscriptionOrderListResponse,
    API.Staff.DoudianSubscriptionOrderListParams
  >("/api/staff/doudian/get_subscription_order_list", params, {
    signal,
  })
}

export const createStaffOrderOpLog = (
  params: API.Staff.CreateStaffOrderOpLogParams,
) => {
  return post<API.Staff.StaffOrderOpLog, API.Staff.CreateStaffOrderOpLogParams>(
    "/api/staff/order/create_staff_order_op_log",
    params,
  )
}

export const listStaffOrders = (
  params: API.Staff.OrderListParams = {},
  signal?: AbortSignal,
) =>
  post<API.Staff.OrderListResponse, API.Staff.OrderListParams>(
    "/api/staff/order/get_order_list",
    params,
    { signal },
  )

export const exportStaffOrders = (
  params: API.Staff.OrderFilterParams = {},
  signal?: AbortSignal,
) =>
  post<API.Staff.ExportOrdersResponse, API.Staff.OrderFilterParams>(
    "/api/staff/order/export_orders",
    params,
    { signal },
  )

export const listStaffOrderOpLogs = (
  params: API.Staff.StaffOrderOpLogListParams = {},
  signal?: AbortSignal,
) => {
  return post<
    API.Staff.StaffOrderOpLogListResponse,
    API.Staff.StaffOrderOpLogListParams
  >("/api/staff/order/get_staff_order_op_log", params, { signal })
}

export const getStaffOrderOpDetail = (
  params: { op_log_id: string },
  signal?: AbortSignal,
) =>
  post<API.Staff.StaffOrderOpDetail>(
    "/api/staff/order/get_staff_order_op_detail",
    params,
    { signal },
  )

export const listOrderRefunds = (
  params: API.Staff.OrderRefundListParams = {},
  signal?: AbortSignal,
) => {
  return post<
    API.Staff.OrderRefundListResponse,
    API.Staff.OrderRefundListParams
  >("/api/staff/order/get_order_refund_list", params, {
    signal,
  })
}

export const getOrderRefundDetail = (
  params: API.Staff.GetOrderRefundDetailParams,
  signal?: AbortSignal,
) => {
  return post<
    API.Staff.OrderRefundDetailResponse,
    API.Staff.GetOrderRefundDetailParams
  >("/api/staff/order/get_order_refund_detail", params, {
    signal,
  })
}

export const requestWalletOrderRefund = (
  params: API.Staff.RequestOrderRefundParams,
) => {
  return post<API.Staff.OrderRefundResult, API.Staff.RequestOrderRefundParams>(
    "/api/staff/order/refund_wallet_order",
    params,
  )
}

export const requestLevelOrderRefund = (
  params: API.Staff.RequestOrderRefundParams,
) => {
  return post<API.Staff.OrderRefundResult, API.Staff.RequestOrderRefundParams>(
    "/api/staff/order/refund_level_order",
    params,
  )
}

export const refreshOrderRefundStatus = (
  params: API.Staff.RefreshOrderRefundStatusParams,
) => {
  return post<
    API.Staff.OrderRefundResult,
    API.Staff.RefreshOrderRefundStatusParams
  >("/api/staff/order/refresh_refund_status", params)
}

export const applyOrderRefund = (params: API.Staff.ApplyOrderRefundParams) => {
  return post<
    API.Staff.ApplyOrderRefundResult,
    API.Staff.ApplyOrderRefundParams
  >("/api/staff/order/apply_refund", params)
}

export const getStaffOrderCleanupDetail = (
  params: API.Staff.GetOrderCleanupDetailParams,
  signal?: AbortSignal,
) => {
  return post<
    API.Staff.OrderCleanupDetailResponse,
    API.Staff.GetOrderCleanupDetailParams
  >("/api/staff/order/get_order_cleanup_detail", params, {
    signal,
  })
}

export const listStaffOrderCleanupOpLogs = (
  params: API.Staff.StaffOrderCleanupOpLogListParams = {},
  signal?: AbortSignal,
) => {
  return post<
    API.Staff.StaffOrderCleanupOpLogListResponse,
    API.Staff.StaffOrderCleanupOpLogListParams
  >("/api/staff/order/get_staff_order_cleanup_op_log", params, {
    signal,
  })
}

export const getStaffOrderCleanupOpDetail = (
  params: API.Staff.StaffOrderCleanupOpLogDetailParams,
  signal?: AbortSignal,
) => {
  return post<
    API.Staff.StaffOrderCleanupOpLogDetailResponse,
    API.Staff.StaffOrderCleanupOpLogDetailParams
  >("/api/staff/order/get_staff_order_cleanup_op_detail", params, {
    signal,
  })
}

export const refundUnusedOrder = (
  params: API.Staff.RefundUnusedOrderParams,
) => {
  return post<
    API.Staff.RefundUnusedOrderResult,
    API.Staff.RefundUnusedOrderParams
  >("/api/staff/order/refund_unused_order", params)
}

export const confirmUnusedOrderRefund = (
  params: API.Staff.ConfirmUnusedOrderRefundParams,
) => {
  return post<
    API.Staff.ConfirmUnusedOrderRefundResult,
    API.Staff.ConfirmUnusedOrderRefundParams
  >("/api/staff/order/confirm_unused_order_refund", params)
}

export const deleteUnusedWalletOrder = (
  params: API.Staff.DeleteUnusedOrderParams,
) => {
  return post<
    API.Staff.DeleteUnusedOrderResult,
    API.Staff.DeleteUnusedOrderParams
  >("/api/staff/order/delete_unused_wallet_order", params)
}

export const deleteUnusedLevelOrder = (
  params: API.Staff.DeleteUnusedOrderParams,
) => {
  return post<
    API.Staff.DeleteUnusedOrderResult,
    API.Staff.DeleteUnusedOrderParams
  >("/api/staff/order/delete_unused_level_order", params)
}

export const getStaffUserByMobile = (
  params: API.Staff.StaffUserByMobileParams,
  signal?: AbortSignal,
) => {
  return post<
    API.Staff.StaffUserInfoResponse,
    API.Staff.StaffUserByMobileParams
  >("/api/staff/user/get_user_by_mobile", params, {
    signal,
  })
}
